/*
 * RefundRequestsTable.tsx (RSX)
 *
 * Copyright © 2026 RSX Technologies GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * @file RefundRequestsTable.tsx
 * @copyright 2026 RSX Technologies GmbH. All rights reserved.
 * @section License
 */

import React, { useState } from 'react';
import { Column } from 'primereact/column';
import BaseDatatable from '@abstract/abstractwebcommon-client/Table/BaseDatatable';
import DatatableColumn from '@abstract/abstractwebcommon-client/Table/DatatableColumn';
import SearchBar from '@abstract/abstractwebcommon-client/SearchBar/SearchBar';
import ConfirmationPopup from '@abstract/abstractwebcommon-client/ConfirmationPopup';
import DialogWrapper from '@abstract/abstractwebcommon-client/DialogWrapper/DialogWrapper';
import { useTranslation } from 'react-i18next';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import {
  IPageEvent,
  ISortEvent,
  ITablePayload
} from '@abstract/abstractwebcommon-shared/interfaces/pagination';
import { currencyFormatter } from '@abstract/abstractwebcommon-shared/utils/currencyFormatter';
import { formatDate } from '@abstract/abstractwebcommon-shared/utils/sharedFunctions';
import { IRefundRequest } from '@abstract/abstractwebcommon-shared/interfaces/ecommerce/RefundRequest';
import { RefundRequestStatus } from '@abstract/abstractwebcommon-shared/enum/ecommerce/refundRequestStatus';

/**
 * Interface for RefundRequestsTable properties.
 */
interface IRefundRequestsTableProperties {
  refundRequests: IRefundRequest[];
  isLoading: boolean;
  isReviewing: boolean /**< True while an approve/reject request is in flight. */;
  tablePayload: ITablePayload;
  totalRecords: number;
  handlePageUpdate: (event: IPageEvent) => void;
  handleSortUpdate: (event: ISortEvent) => void;
  handleFilterUpdate: (event: string) => void;
  approveRefundRequest: (refundRequestID: string) => void;
  rejectRefundRequest: (
    refundRequestID: string,
    rejectionReason: string
  ) => void;
}

const RefundRequestsTable = (properties: IRefundRequestsTableProperties) => {
  const translation = useTranslation().t;
  const sort: ISortEvent = properties.tablePayload?.sort;

  const [
    isApproveConfirmationVisible,
    setApproveConfirmationVisible
  ] = useState<boolean>(false);
  const [confirmPopupTarget, setConfirmPopupTarget] = useState<any>(null);
  const [
    isRejectFormVisible,
    setRejectFormVisible
  ] = useState<boolean>(false);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [
    selectedRefundRequestID,
    setSelectedRefundRequestID
  ] = useState<string | null>(null);

  const isPending = (rowData: IRefundRequest): boolean =>
    rowData.status === RefundRequestStatus.Pending;

  /// Header with search bar.
  const header = (
    <div className="d-flex justify-content-between align-items-center">
      <div className="headerTableContainer"></div>
      <SearchBar
        onSearchTermChanged={(data) => properties.handleFilterUpdate(data)}
      />
    </div>
  );

  /// Open the approve confirmation popup.
  const openApproveConfirmation = (
    event: any,
    refundRequestID: string
  ): void => {
    setSelectedRefundRequestID(refundRequestID);
    setConfirmPopupTarget(event.target);
    setApproveConfirmationVisible(true);
  };

  /// Accept the approve confirmation.
  const onAcceptApprove = (): void => {
    setApproveConfirmationVisible(false);
    if (selectedRefundRequestID) {
      properties.approveRefundRequest(selectedRefundRequestID);
    }
  };

  /// Open the reject reason form.
  const openRejectForm = (refundRequestID: string): void => {
    setSelectedRefundRequestID(refundRequestID);
    setRejectionReason('');
    setRejectFormVisible(true);
  };

  /// Close the reject reason form.
  const closeRejectForm = (): void => {
    setRejectFormVisible(false);
    setRejectionReason('');
  };

  /// Submit the rejection.
  const onSubmitReject = (): void => {
    if (selectedRefundRequestID) {
      properties.rejectRefundRequest(
        selectedRefundRequestID,
        rejectionReason.trim()
      );
    }
    closeRejectForm();
  };

  return (
    <>
      <BaseDatatable
        value={properties.refundRequests}
        isLoading={properties.isLoading}
        dataKey="_id"
        sortField={sort && sort.sortField}
        sortOrder={sort && sort.sortOrder}
        first={properties.tablePayload.skip}
        rows={properties.tablePayload.limit}
        onPage={properties.handlePageUpdate}
        onSort={properties.handleSortUpdate}
        header={header}
        totalRecords={properties.totalRecords}
        emptyMessage={translation('refundRequests.table.empty_message')}
      >
        <Column
          field="customerFullName"
          header={translation('refundRequests.table.customer')}
          sortable
          body={(rowData: IRefundRequest) => (
            <DatatableColumn
              title={translation('refundRequests.table.customer')}
              data={rowData.customerFullName ?? rowData.customerEmail}
            />
          )}
        />
        <Column
          field="productName"
          header={translation('refundRequests.table.product')}
          body={(rowData: IRefundRequest) => (
            <DatatableColumn
              title={translation('refundRequests.table.product')}
              data={rowData.productName}
            />
          )}
        />
        <Column
          field="amount"
          header={translation('refundRequests.table.amount')}
          headerClassName="d-table-cell d-sm-none d-xl-table-cell"
          className="d-table-cell d-sm-none d-xl-table-cell"
          body={(rowData: IRefundRequest) => (
            <DatatableColumn
              title={translation('refundRequests.table.amount')}
              data={
                rowData.amount != null
                  ? currencyFormatter(Number(rowData.amount))
                  : ''
              }
            />
          )}
        />
        <Column
          field="reason"
          header={translation('refundRequests.table.reason')}
          headerClassName="d-table-cell d-sm-none d-xl-table-cell"
          className="d-table-cell d-sm-none d-xl-table-cell"
          body={(rowData: IRefundRequest) => (
            <DatatableColumn
              title={translation('refundRequests.table.reason')}
              isEllipsisVisible={false}
              data={
                <span
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {rowData.reason}
                </span>
              }
            />
          )}
        />
        <Column
          field="createdAt"
          header={translation('refundRequests.table.requestDate')}
          headerClassName="createdDateCol d-table-cell d-sm-none d-xxl-table-cell"
          className="createdDateCol d-table-cell d-sm-none d-xxl-table-cell"
          sortable
          body={(rowData: IRefundRequest) => (
            <DatatableColumn
              title={translation('refundRequests.table.requestDate')}
              data={rowData.createdAt ? formatDate(rowData.createdAt) : ''}
            />
          )}
        />
        <Column
          field="status"
          header={translation('refundRequests.table.status')}
          sortable
          body={(rowData: IRefundRequest) => (
            <DatatableColumn
              title={translation('refundRequests.table.status')}
              data={translation(
                `refundRequests.status.${(rowData.status ?? '').toLowerCase()}`,
                { defaultValue: rowData.status }
              )}
            />
          )}
        />
        <Column
          field="approve"
          className="absolute-position-responsive-screen col-width-45 p-2"
          body={(rowData: IRefundRequest) => (
            <div className="d-flex justify-content-center version-button-container">
              <Button
                className={`${
                  isPending(rowData) && !properties.isReviewing
                    ? ''
                    : 'custom-disabled-button'
                } custom-action-column-action-position`}
                style={{ minWidth: '80px' }}
                onClick={(event) => {
                  if (isPending(rowData) && !properties.isReviewing) {
                    openApproveConfirmation(event, rowData._id as string);
                  }
                }}
                disabled={!isPending(rowData) || properties.isReviewing}
              >
                {translation('refundRequests.table.approve_button')}
              </Button>
            </div>
          )}
        />
        <Column
          field="reject"
          className="absolute-position-responsive-screen col-width-45 p-2"
          body={(rowData: IRefundRequest) => (
            <div className="d-flex justify-content-center version-button-container">
              <Button
                variant="danger"
                className={`${
                  isPending(rowData) && !properties.isReviewing
                    ? ''
                    : 'custom-disabled-button'
                } custom-action-column-action-position`}
                style={{ minWidth: '80px' }}
                onClick={() => {
                  if (isPending(rowData) && !properties.isReviewing) {
                    openRejectForm(rowData._id as string);
                  }
                }}
                disabled={!isPending(rowData) || properties.isReviewing}
              >
                {translation('refundRequests.table.reject_button')}
              </Button>
            </div>
          )}
        />
      </BaseDatatable>

      <ConfirmationPopup
        target={confirmPopupTarget}
        isShow={isApproveConfirmationVisible}
        title={translation('refundRequests.confirm.approve')}
        onAccept={onAcceptApprove}
        onReject={() => setApproveConfirmationVisible(false)}
        acceptBtnClass="danger"
        rejectBtnClass="secondary"
        rejectLabel={translation('/confirm_messages.no')}
        acceptLabel={translation('/confirm_messages.yes')}
        acceptBtnIcon="bi bi-check2-circle"
        rejectBtnIcon="bi bi-x-circle"
      />

      <DialogWrapper
        isDialogVisible={isRejectFormVisible}
        onHide={closeRejectForm}
        headerTitle={translation('refundRequests.reject_form.header_title')}
      >
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmitReject();
          }}
        >
          <Form.Group className="mb-3">
            <Form.Label>
              {translation('refundRequests.reject_form.reason_label')}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={rejectionReason}
              maxLength={1000}
              placeholder={translation(
                'refundRequests.reject_form.reason_placeholder'
              )}
              onChange={(event) => setRejectionReason(event.target.value)}
            />
          </Form.Group>
          <div className="d-flex align-items-center justify-content-between">
            <Button
              type="button"
              variant="secondary"
              className="d-flex align-items-center"
              onClick={closeRejectForm}
            >
              <i className="bi bi-x-circle btn-icon"></i>
              {translation('/confirm_messages.no')}
            </Button>
            <Button
              type="submit"
              variant="danger"
              className="d-flex align-items-center m-0"
              disabled={properties.isReviewing}
            >
              <i className="bi bi-check2-circle btn-icon"></i>
              {translation('refundRequests.reject_form.submit_button')}
            </Button>
          </div>
        </Form>
      </DialogWrapper>
    </>
  );
};

export default RefundRequestsTable;
